'use client'

import { ComponentType, useEffect, useRef } from 'react'

const SDK_SCRIPT_ID = 'facebook-jssdk'
const FB_ROOT_ID = 'fb-root'
const OAUTH_STATE = 'facebookdirect'

type FacebookOauthParams = {
	client_id: string
	redirect_uri: string
	state: string
	return_scopes: boolean
	scope: string
	auth_type?: string
}

type FacebookAuthResponse = {
	accessToken?: string
	expiresIn?: number
	signedRequest?: string
	userID?: string
	graphDomain?: string
	grantedScopes?: string
	data_access_expiration_time?: number
}

type FacebookLoginResponse = {
	status?: string
	authResponse?: FacebookAuthResponse | null
}

type FacebookSdk = {
	init: (options: {
		version: string
		appId: string
		xfbml: boolean
		cookie: boolean
	}) => void
	login: (
		callback: (response: FacebookLoginResponse) => void,
		options?:
			| { scope: string; return_scopes: boolean; auth_type?: string }
			| boolean,
	) => void
	getLoginStatus: (
		callback: (response: FacebookLoginResponse) => void,
	) => void
	api: (
		path: string,
		params: { locale: string; fields: string },
		callback: (profile: Record<string, unknown>) => void,
	) => void
}

type FacebookWindow = Window &
	typeof globalThis & {
		FB: FacebookSdk
		fbAsyncInit?: () => void
	}

type FacebookAuthClickEvent = {
	defaultPrevented?: boolean
}

interface FacebookAuthProps<TCallbackResponse> {
	appId: string
	callback: (response: TCallbackResponse) => void
	component: ComponentType<{ onClick: () => void }>
	autoLoad?: boolean
	cookie?: boolean
	customProps?: Record<string, unknown>
	disableRedirect?: boolean
	fields?: string
	isMobile?: boolean
	language?: string
	onClick?: (event: FacebookAuthClickEvent) => void
	onFailure?: (response: { status?: string }) => void
	reAuthenticate?: boolean
	redirectUri?: string
	reRequest?: boolean
	returnScopes?: boolean
	scope?: string
	version?: string
	xfbml?: boolean
}

const facebookWindow = () => window as FacebookWindow

const toQueryString = (params: FacebookOauthParams) =>
	Object.entries(params)
		.map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
		.join('&')

const isMobileDevice = () => {
	try {
		const standaloneNavigator = window.navigator as Navigator & {
			standalone?: boolean
		}

		return Boolean(
			standaloneNavigator.standalone ||
				navigator.userAgent.match('CriOS') ||
				navigator.userAgent.match(/mobile/i),
		)
	} catch {
		return false
	}
}

const loadSdkAsynchronously = (language: string) => {
	const firstScript = document.getElementsByTagName('script')[0]

	if (document.getElementById(SDK_SCRIPT_ID)) {
		return
	}

	const script = document.createElement('script')
	script.id = SDK_SCRIPT_ID
	script.src = `https://connect.facebook.net/${language}/sdk.js`
	firstScript?.parentNode?.insertBefore(script, firstScript)
}

export const FacebookAuth = <TCallbackResponse,>({
	appId,
	callback,
	component: Component,
	autoLoad = false,
	cookie = false,
	customProps = {},
	disableRedirect = false,
	fields = 'name,email,picture',
	isMobile = isMobileDevice(),
	language = 'en_US',
	onClick,
	onFailure,
	reAuthenticate = false,
	redirectUri = typeof window !== 'undefined' ? window.location.href : '/',
	reRequest = false,
	returnScopes = false,
	scope = 'public_profile,email',
	version = '2.8',
	xfbml = false,
}: FacebookAuthProps<TCallbackResponse>) => {
	const responseApi = (authResponse: FacebookAuthResponse) => {
		facebookWindow().FB.api(
			'/me',
			{ locale: language, fields },
			(profile) => {
				callback({
					...profile,
					...authResponse,
				} as TCallbackResponse)
			},
		)
	}

	const checkLoginState = (response: FacebookLoginResponse) => {
		if (response.authResponse) {
			responseApi(response.authResponse)
			return
		}

		if (onFailure) {
			onFailure({ status: response.status })
		} else {
			callback({ status: response.status } as TCallbackResponse)
		}
	}

	const checkLoginAfterRefresh = (response: FacebookLoginResponse) => {
		if (response.status === 'unknown') {
			facebookWindow().FB.login(
				(loginResponse) => checkLoginState(loginResponse),
				true,
			)
		} else {
			checkLoginState(response)
		}
	}

	const checkLoginAfterRefreshRef = useRef(checkLoginAfterRefresh)
	checkLoginAfterRefreshRef.current = checkLoginAfterRefresh

	useEffect(() => {
		if (document.getElementById(SDK_SCRIPT_ID)) {
			return
		}

		facebookWindow().fbAsyncInit = () => {
			facebookWindow().FB.init({
				version: `v${version}`,
				appId,
				xfbml,
				cookie,
			})

			if (
				autoLoad ||
				window.location.search.indexOf(OAUTH_STATE) !== -1
			) {
				facebookWindow().FB.getLoginStatus((response) =>
					checkLoginAfterRefreshRef.current(response),
				)
			}
		}

		loadSdkAsynchronously(language)

		if (!document.getElementById(FB_ROOT_ID)) {
			const rootElement = document.createElement('div')
			rootElement.id = FB_ROOT_ID
			document.body.appendChild(rootElement)
		}
	}, [appId, autoLoad, cookie, language, version, xfbml])

	const handleClick = (event: FacebookAuthClickEvent = {}) => {
		if (typeof onClick === 'function') {
			onClick(event)

			if (event.defaultPrevented) {
				return
			}
		}

		const params: FacebookOauthParams = {
			client_id: appId,
			redirect_uri: redirectUri,
			state: OAUTH_STATE,
			return_scopes: returnScopes,
			scope,
		}

		if (reAuthenticate) {
			params.auth_type = 'reauthenticate'
		}

		if (reRequest) {
			params.auth_type = 'rerequest'
		}

		if (isMobile && !disableRedirect) {
			window.location.href = `//www.facebook.com/dialog/oauth?${toQueryString(params)}`
			return
		}

		facebookWindow().FB.login(checkLoginState, {
			scope,
			return_scopes: returnScopes,
			auth_type: params.auth_type,
		})
	}

	return <Component onClick={handleClick} {...customProps} />
}
